import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { fmtMoney2, monthLabel, useCollections } from "../api";

/**
 * The "not getting paid" view: contracts whose invoices are unpaid, holding
 * commission. AEs see their own holds so they can help collect; when the
 * invoice pays, the held months pay out as catch-ups in the next run.
 */
export function CollectionsView() {
  const { data, error, isLoading } = useCollections();

  return (
    <div className="flex flex-col gap-4">
      {error != null && (
        <Alert variant="danger">
          <AlertTitle>Couldn't load collections</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      )}

      {isLoading && !data && <Card className="h-32 animate-pulse" />}

      {data && data.length === 0 && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <AlertTriangle className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">Nothing held</p>
          <p className="max-w-md text-sm text-muted-foreground">
            No commissions are currently held for unpaid invoices. When a customer's
            invoice goes unpaid, the held commission shows here — and pays out
            automatically (both months) once cash arrives.
          </p>
        </Card>
      )}

      {data && data.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="flex items-center gap-2 border-b border-border bg-warning-light px-4 py-3">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <span className="text-sm font-medium">
              {data.length} contract{data.length === 1 ? "" : "s"} with commission held on
              unpaid invoices — helping collect gets these paid.
            </span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payee</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Held since</TableHead>
                  <TableHead className="text-right">Months held</TableHead>
                  <TableHead className="text-right">Commission held</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((h, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{h.payee_name}</TableCell>
                    <TableCell className="max-w-[260px] truncate" title={h.company_name ?? ""}>
                      {h.company_name ?? "—"}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {h.invoice_document_id ?? "—"}
                    </TableCell>
                    <TableCell>{monthLabel(h.first_held_period)}</TableCell>
                    <TableCell className="text-right tabular-nums">{h.months_held}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums text-warning">
                      {fmtMoney2(h.held_total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
